"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible } from "@/components/ui/collapsible";
import { Field, FieldContent } from "@/components/ui/field";
import type { SearchFacets } from "@/lib/models/search";
import { DownloadCloudIcon, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Switch } from "@/components/ui/Switch";
import { Select } from "@/components/ui/Select";
import { offices, orderByOptions } from "@/lib/utils/Offices";
import { useSession } from "next-auth/react";
import { useApi } from "@/lib/hooks/useApi";
import {
  canDownloadPriceList,
  isPublicSession,
} from "@/lib/utils/pricingVisibility";
import { useDictionaryStore } from "@/lib/store/dictionaryStore";
import { getCategoryDisplayName } from "@/lib/utils/categoryHelpers";
import type { BlobExportResponse } from "@/lib/models/apiResponse";

export interface FiltersClientProps {
  facets?: SearchFacets;
  onFilterChange?: (
    filters: Record<string, string[]>,
    orderValue?: string,
  ) => void;
}

interface categoriesTreeType {
  name: string;
  path: string;
  slug: string;
}

interface FacetOption {
  name?: string;
  path?: string;
  marcaName?: string;
  marcaCode?: number;
  tags?: string;
}

/**
 * Componente de filtros que renderiza los facets disponibles
 */
export function FiltersClient({
  facets = {},
  onFilterChange,
}: FiltersClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { categoriesMenu } = useDictionaryStore();
  const { data: session } = useSession();
  const { apiRequest } = useApi("");
  const formatFacets = (facetsList: SearchFacets) => {
    const obj: Record<string, FacetOption[]> = {};
    Object.assign(obj, facetsList);
    return obj;
  };

  const [allFacets, setallFacets] = useState<Record<string, FacetOption[]>>(
    () => ({
      sort: [],
      ...formatFacets(facets),
    }),
  );
  const getFiltersFromUrl = () => {
    const keys = ["marcaCodes", "tags"];
    const filters: Record<string, string[]> = {};
    keys.forEach((key) => {
      const param = searchParams.get(key);
      if (!param) return;
      try {
        const arr = JSON.parse(param);
        if (Array.isArray(arr)) {
          filters[key] = arr.map(String);
        }
      } catch {
        // ignorar
      }
    });
    return filters;
  };
  const [selectedFilters, setSelectedFilters] =
    useState<Record<string, string[]>>(getFiltersFromUrl());

  // Sincronizar selectedFilters con la URL cuando searchParams cambie
  useEffect(() => {
    setSelectedFilters(getFiltersFromUrl());
  }, [searchParams]);

  const keysToSearch = ["marcas", "tags"];
  const [search, setSearch] = useState<Record<string, string>>({});

  // Sincronizar el valor del select de orden con la URL
  useEffect(() => {
    const sortValue = searchParams.get("sort") || "relevancia";
    setSearch((prev) => ({ ...prev, sort: sortValue }));
  }, [searchParams]);
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const [categoriesTree, setCategoriesTree] = useState<categoriesTreeType[]>(
    [],
  );

  function handleDownloadPrices() {
    apiRequest<BlobExportResponse>("pricing/export", {
      method: "get",
      loadingMessage:
        "Exportando precios, este proceso puede tomar unos minutos...",
      responseType: "blob",
      returnFullResponse: true,
    })
      .then((res) => {
        const blob = res.data;
        const contentDisposition = res.headers["content-disposition"];
        let fileName = `precios_${Date.now()}.xlsx`;

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) fileName = match[1];
        } else {
          const now = new Date();
          const timestamp =
            now.getFullYear().toString() +
            (now.getMonth() + 1).toString().padStart(2, "0") +
            now.getDate().toString().padStart(2, "0") +
            "_" +
            now.getHours().toString().padStart(2, "0") +
            now.getMinutes().toString().padStart(2, "0") +
            now.getSeconds().toString().padStart(2, "0");
          fileName = `precios_${timestamp}.xlsx`;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.error("Error al exportar precios", err);
      });
  }

  useEffect(() => {
    if (facets && Object.keys(facets).length > 0) {
      setallFacets({ sort: [], ...formatFacets(facets) });
    }
  }, [facets]);

  const isPublic = isPublicSession(session);

  useEffect(() => {
    if (isPublic && searchParams.get("inStockWarehouse05Only") === "true") {
      const params = new URLSearchParams(Array.from(searchParams.entries()));
      params.delete("inStockWarehouse05Only");
      router.replace("?" + params.toString());
    }
  }, [isPublic, searchParams, router]);

  const optionsSwitch: Record<string, string> = {
    isNew: "Nuevo",
    isFeatured: "Destacado",
    inStockWarehouse05Only: "En liquidación",
    inStockTotalOnly: "En existencia",
    isKit: "Kits",
    hasDiscountPeriodQuantityPriceList1Only: "Promoción",
  };

  if (isPublic) {
    delete optionsSwitch.inStockWarehouse05Only;
  }

  if (
    session?.user?.tokenDecode?.lstPrice === 3 ||
    session?.user?.tokenDecode?.lstPrice === 5
  ) {
    if (session?.user?.tokenDecode?.lstPrice === 3) {
      optionsSwitch["hasDiscountPeriodQuantityPriceList3Only"] = "Promoción";
    }
    if (session?.user?.tokenDecode?.lstPrice === 5) {
      optionsSwitch["hasDiscountPeriodQuantityPriceList5Only"] = "Promoción";
    }
    delete optionsSwitch.hasDiscountPeriodQuantityPriceList1Only;
  } else {
    delete optionsSwitch.hasDiscountPeriodQuantityPriceList3Only;
    delete optionsSwitch.hasDiscountPeriodQuantityPriceList5Only;
  }

  // Limpia de la URL los parámetros de "Promoción" que no correspondan al
  // lstPrice actual del usuario. Antes esto llamaba a router.replace()
  // directo en el cuerpo del render (en cada render, no solo cuando la
  // sesión/URL cambian) - efecto secundario fuera de un useEffect.
  useEffect(() => {
    const lstPrice = session?.user?.tokenDecode?.lstPrice;
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    let changed = false;

    if (lstPrice === 3 || lstPrice === 5) {
      if (params.get("hasDiscountPeriodQuantityPriceList1Only") === "true") {
        params.delete("hasDiscountPeriodQuantityPriceList1Only");
        changed = true;
      }
    } else {
      if (params.get("hasDiscountPeriodQuantityPriceList3Only") === "true") {
        params.delete("hasDiscountPeriodQuantityPriceList3Only");
        changed = true;
      }
      if (params.get("hasDiscountPeriodQuantityPriceList5Only") === "true") {
        params.delete("hasDiscountPeriodQuantityPriceList5Only");
        changed = true;
      }
    }

    if (changed) {
      router.replace("?" + params.toString());
    }
  }, [session, searchParams, router]);

  const allSwitchKeys = [
    "isNew",
    "isFeatured",
    "inStockWarehouse05Only",
    "inStockTotalOnly",
    "isKit",
    "hasDiscountPeriodQuantityPriceList1Only",
    "hasDiscountPeriodQuantityPriceList3Only",
    "hasDiscountPeriodQuantityPriceList5Only",
  ];

  const hasActiveFilters =
    Object.keys(selectedFilters).length > 0 ||
    Boolean(searchParams.get("marcaCodes")) ||
    Boolean(searchParams.get("tags")) ||
    Boolean(searchParams.get("office")) ||
    Boolean(searchParams.get("categoryPath")) ||
    Boolean(
      searchParams.get("sort") && searchParams.get("sort") !== "relevancia",
    ) ||
    allSwitchKeys.some((key) => searchParams.get(key) === "true");

  const handleClearAllFilters = () => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));

    params.delete("marcaCodes");
    params.delete("tags");
    Object.keys(selectedFilters).forEach((key) => params.delete(key));

    allSwitchKeys.forEach((key) => params.delete(key));

    params.delete("office");
    params.delete("categoryPath");
    params.delete("sort");
    params.delete("page");

    router.replace("?" + params.toString());
    setSelectedFilters({});
    setSearch((prev) => ({
      ...prev,
      sort: "relevancia",
      marcaNames: "",
      tags: "",
    }));
    if (onFilterChange) {
      onFilterChange({}, "relevancia");
    }
  };

  const handleFilterChange = (
    facetName: string,
    value: string,
    checked: boolean,
  ) => {
    setSelectedFilters((prev) => {
      const newFilters = { ...prev };
      const current = prev[facetName] || [];
      const updated = checked
        ? [...current, value]
        : current.filter((v) => v !== value);
      if (updated.length > 0) {
        newFilters[facetName] = updated;
      } else {
        delete newFilters[facetName];
      }
      // Actualizar la URL para el filtro
      const params = new URLSearchParams(Array.from(searchParams.entries()));
      if (newFilters[facetName] && newFilters[facetName].length > 0) {
        if (facetName === "marcaCodes") {
          const numCodes = newFilters[facetName]
            .map(Number)
            .filter((n) => !isNaN(n));
          params.set(facetName, JSON.stringify(numCodes));
        } else {
          params.set(facetName, JSON.stringify(newFilters[facetName]));
        }
      } else {
        params.delete(facetName);
      }
      router.replace("?" + params.toString());
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
      return newFilters;
    });
  };

  // Reconstruct categoriesTree from categoryPath URL parameter
  useEffect(() => {
    const categoryPath = searchParams.get("categoryPath");
    if (categoryPath) {
      const parts = categoryPath.split("/").filter(Boolean);
      const newTree = parts.map((slug, idx) => {
        const path = parts.slice(0, idx + 1).join("/");
        const name = getCategoryDisplayName(categoriesMenu, path, slug);
        return {
          name,
          path,
          slug,
        };
      });
      setCategoriesTree(newTree);
    } else {
      setCategoriesTree([]);
    }
  }, [searchParams, categoriesMenu]);

  if (!allFacets || Object.keys(allFacets).length === 0) {
    return (
      <Card className="p-4">
        <h2 className="font-bold mb-4">Filtros</h2>
        <p className="text-sm text-muted-foreground">
          No hay filtros disponibles
        </p>
      </Card>
    );
  }
  const traductionsRecomendations: Record<string, string> = {
    marcas: "Marcas",
    tags: "Filtros",
    sort: "Ordenamiento",
    categoriesNextLevel: "Categorías",
  };

  function categoriesPathFormatter(
    itemFilter: { path?: string; slug?: string; name?: string },
    checked: boolean,
    index: number,
  ) {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    if (checked && itemFilter.path) {
      params.set("categoryPath", itemFilter.path);
    } else {
      if (index > 0) {
        const parentPath = categoriesTree
          .slice(0, index)
          .map((c) => c.slug)
          .join("/");
        params.set("categoryPath", parentPath);
      } else {
        params.delete("categoryPath");
      }
    }
    router.replace("?" + params.toString());
  }

  function createContent(facet: string) {
    const isCategoriesEmpty =
      facet === "categoriesNextLevel" &&
      (!allFacets[facet] || allFacets[facet].length === 0) &&
      categoriesTree.length === 0;

    const isOtherEmpty =
      facet !== "sort" &&
      facet !== "categoriesNextLevel" &&
      (!allFacets[facet] || allFacets[facet].length === 0);

    if (isCategoriesEmpty || isOtherEmpty) {
      return null;
    }
    switch (facet) {
      case "sort":
        return (
          <div className="mb-4">
            <Select
              style="default"
              id="order-select"
              className="mb-3"
              value={search[facet] || searchParams.get("sort") || "relevancia"}
              options={orderByOptions}
              label={"Ordenar por"}
              onChange={(e) => {
                const value = String(e);
                setSearch((prev) => ({ ...prev, [facet]: value }));
                const params = new URLSearchParams(
                  Array.from(searchParams.entries()),
                );
                if (value === "relevancia") {
                  params.delete("sort");
                } else {
                  params.set("sort", value);
                }
                router.replace("?" + params.toString());
                onFilterChange?.(selectedFilters, value);
              }}
            />
            <Select
              id="business-select"
              value={searchParams.get("office") || ""}
              className="mb-3"
              style="default"
              options={offices}
              label={"Sucursales"}
              onChange={(e) => {
                const value = e as string;
                const params = new URLSearchParams(
                  Array.from(searchParams.entries()),
                );
                if (value === "" || value === "relevancia") {
                  params.delete("office");
                } else {
                  params.set("office", value);
                }
                router.replace("?" + params.toString());
              }}
            />
          </div>
        );
      case "marcas":
        return (
          <Field orientation="horizontal" className="mb-3">
            <FieldContent className="gap-0">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder={`Buscar marca...`}
                  value={search?.marcaNames || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({
                      ...prev,
                      ["marcaNames"]: e.target.value,
                    }))
                  }
                  className="border border-gray-300 w-full px-4 py-2 pl-10 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </FieldContent>
          </Field>
        );
      case "tags":
        return (
          <Field orientation="horizontal" className="mb-3">
            <FieldContent className="gap-0">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder={`Buscar...`}
                  value={search?.tags || ""}
                  onChange={(e) =>
                    setSearch((prev) => ({
                      ...prev,
                      ["tags"]: e.target.value,
                    }))
                  }
                  className="border border-gray-300 w-full px-4 py-2 pl-10 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </FieldContent>
          </Field>
        );
      case "categoriesNextLevel":
        const nextLevel = (allFacets[facet] as FacetOption[]) || [];
        const nextLevelFiltered = nextLevel.filter(
          (item: FacetOption) =>
            !categoriesTree.some((c) => c.path === item.path),
        );
        const allFacetsMap = [...categoriesTree, ...nextLevelFiltered];
        return (
          <div className="gap-2 flex flex-col">
            {allFacetsMap.map((item) => {
              const findIndex = categoriesTree.findIndex(
                (categoria) => categoria.path === item.path,
              );

              return (
                <Checkbox
                  variant="default"
                  label={item.name}
                  key={item.path}
                  checked={findIndex !== -1}
                  onCheckedChange={(checked) => {
                    categoriesPathFormatter(item, Boolean(checked), findIndex);
                  }}
                />
              );
            })}
          </div>
        );
      default:
        break;
    }
  }
  function handleCheck(type: string) {
    // Alternar el valor booleano en la URL
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    const current = params.get(type) === "true";
    if (current) {
      params.delete(type);
    } else {
      params.set(type, "true");
    }
    router.replace("?" + params.toString());
  }
  return (
    <div
      className="
        w-full
        md:max-w-xs
        rounded-lg
        md:mb-0
        overflow-x-auto
        md:overflow-visible
        flex flex-col
        gap-2
        bg-white
      "
    >
      {/* Switches/checkboxes en la parte superior sin colapsarse */}
      <div className="bg-white  p-2 flex flex-col gap-2 ">
        {Object.entries(optionsSwitch).map(([key, label]) => {
          return (
            <div key={key} className="flex items-center space-x-2">
              <Checkbox
                id={key}
                label={label}
                onCheckedChange={() => handleCheck(key)}
                checked={searchParams.get(key) === "true"}
              />
            </div>
          );
        })}
      </div>

      <Collapsible
        sections={Object.keys(allFacets).map((facet) => ({
          title: traductionsRecomendations[facet] || facet.toLocaleUpperCase(),
          content: (
            <div className="space-y-3">
              {createContent(facet)}
              {/* Filtrado y paginación de items */}
              {facet !== "sort" &&
                facet !== "categoriesNextLevel" &&
                (function () {
                  const filtered = allFacets[facet]?.filter(
                    (option: FacetOption) => {
                      const optionName =
                        option.marcaName || option.tags || option.name || "";
                      const optionCode = option.marcaCode;
                      if (facet === "marcas") {
                        const val = search.marcaNames || "";
                        return (
                          optionName
                            .toLowerCase()
                            .includes(val.toLowerCase()) ||
                          optionCode?.toString().includes(val)
                        );
                      }
                      if (!keysToSearch.includes(facet)) return true;

                      const val = search[facet] || "";

                      return optionName
                        ? optionName.toLowerCase().includes(val.toLowerCase())
                        : false;
                    },
                  );
                  const showAllFacet = showAll[facet];
                  const visible = showAllFacet
                    ? filtered
                    : filtered.slice(0, 10);
                  return (
                    <>
                      <div className="flex flex-col gap-2">
                        {visible.map((option: FacetOption) => {
                          const optionName =
                            option.marcaName ||
                            option.tags ||
                            option.name ||
                            "";
                          const optionCode = option.marcaCode;
                          let isChecked = false;
                          if (facet === "marcas") {
                            isChecked =
                              selectedFilters.marcaCodes?.includes(
                                String(optionCode),
                              ) || false;
                          } else {
                            isChecked =
                              selectedFilters[facet]?.includes(optionName) ||
                              false;
                          }
                          return (
                            <div
                              key={`${facet}-${optionCode || optionName}`}
                              className="flex justify-between items-center"
                            >
                              <Checkbox
                                label={optionName}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  if (facet === "marcas") {
                                    handleFilterChange(
                                      "marcaCodes",
                                      String(optionCode),
                                      !!checked,
                                    );
                                  } else {
                                    handleFilterChange(
                                      facet,
                                      optionName,
                                      !!checked,
                                    );
                                  }
                                }}
                              />
                            </div>
                          );
                        })}
                      </div>
                      {keysToSearch.includes(facet) && filtered.length > 10 && (
                        <div className="flex justify-center mt-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full md:w-auto"
                            onClick={() =>
                              setShowAll((prev) => ({
                                ...prev,
                                [facet]: !prev[facet],
                              }))
                            }
                          >
                            {showAllFacet ? "Mostrar menos" : "Mostrar más"}
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          ),
        }))}
      />
      {canDownloadPriceList(session) && (
        <Button
          variant="outline"
          // size="sm"
          // className="border !text-secondary flex px-4 py-2.5 gap-1.5 justify-center items-center text-sm font-medium cursor-pointer rounded-lg !text-md !font-semibold "
          onClick={() => handleDownloadPrices()}
        >
          <DownloadCloudIcon />
          Descargar precios
        </Button>
      )}
      {hasActiveFilters && (
        <Button onClick={handleClearAllFilters}>Limpiar filtros</Button>
      )}
    </div>
  );
}
